// src/components/admin/CostSettingsModal.js

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoClose, IoSaveOutline, IoCashOutline } from 'react-icons/io5';

const COST_SETTINGS_KEY = 'adminCostSettings';

const CostInput = ({ label, value, onChange, placeholder }) => (
    <div>
        <label className="block text-sm font-medium text-s-text mb-1">{label}</label>
        <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-s-text">$</span>
            <input
                type="number"
                step="0.01"
                value={value}
                onChange={e => onChange(parseFloat(e.target.value) || 0)}
                placeholder={placeholder}
                className="w-full bg-2 border border-line rounded-lg pl-7 pr-3 py-2 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            />
        </div>
    </div>
);

export const CostSettingsModal = ({ isOpen, onClose, onSave }) => {
    const [settings, setSettings] = useState({
        tasks: { input: 0, output: 0 },
        workflows: { input: 0, output: 0 },
    });

    useEffect(() => {
        if (isOpen) {
            const savedSettings = JSON.parse(localStorage.getItem(COST_SETTINGS_KEY));
            if (savedSettings) {
                setSettings(savedSettings);
            }
        }
    }, [isOpen]);

    const handleSave = () => {
        localStorage.setItem(COST_SETTINGS_KEY, JSON.stringify(settings));
        onSave(settings);
        toast.success('Cost settings saved!');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex justify-center items-center p-4 backdrop-blur-sm">
            <div className="bg-1 w-full max-w-lg rounded-2xl shadow-2xl flex flex-col animate-fade-in-up">
                <div className="flex justify-between items-center p-4 border-b border-line">
                    <h2 className="text-xl font-bold text-p-text flex items-center gap-2">
                        <IoCashOutline /> Configure Token Costs
                    </h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-2"><IoClose className="h-5 w-5"/></button>
                </div>
                <div className="p-6 space-y-6">
                    <p className="text-sm text-s-text">
                        Enter the cost in US Dollars ($) per 1 million tokens for each category. These values are saved locally in your browser.
                    </p>
                    <div className="p-4 bg-body rounded-lg space-y-4 border border-line">
                        <h3 className="font-semibold text-p-text">Task Usage Costs</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CostInput label="Input Cost ($ / 1M tokens)" value={settings.tasks.input} onChange={v => setSettings(s => ({ ...s, tasks: { ...s.tasks, input: v } }))} placeholder="e.g., 0.50" />
                            <CostInput label="Output Cost ($ / 1M tokens)" value={settings.tasks.output} onChange={v => setSettings(s => ({ ...s, tasks: { ...s.tasks, output: v } }))} placeholder="e.g., 1.50" />
                        </div>
                    </div>
                     <div className="p-4 bg-body rounded-lg space-y-4 border border-line">
                        <h3 className="font-semibold text-p-text">Workflow Usage Costs</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <CostInput label="Input Cost ($ / 1M tokens)" value={settings.workflows.input} onChange={v => setSettings(s => ({ ...s, workflows: { ...s.workflows, input: v } }))} placeholder="e.g., 1.00" />
                            <CostInput label="Output Cost ($ / 1M tokens)" value={settings.workflows.output} onChange={v => setSettings(s => ({ ...s, workflows: { ...s.workflows, output: v } }))} placeholder="e.g., 3.00" />
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-2 rounded-b-2xl flex justify-end">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#5652CF] text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors">
                        <IoSaveOutline /> Save Settings
                    </button>
                </div>
            </div>
        </div>
    );
};